import React, { useState, useEffect } from "react";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DeleteModal from "../../components/DeleteBlogModal";
import { apiRequestHandler } from "../../apiConfig/service";
import { toast } from "react-toastify";
import { url } from "../../apiConfig/ApiConfig";
import { formatDate } from "../../utils/formatedDate";
import Loader from "../../components/Loader";
import DataTable from "react-data-table-component";

const getAllDepartments = async () => {
  const res = await apiRequestHandler({
    endPoint: "allDepartment",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res?.department;
};

const DepartmentManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formMode, setFormMode] = useState("add");

  const validationSchema = Yup.object({
    departmentName: Yup.string().required("Department title is required"),
  });

  useEffect(() => {
    setIsLoading(true);
    getAllDepartments()
      .then((res) => setDepartments(res || []))
      .catch(() => toast.error("Failed to load departments"))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const filtered = departments.filter((d) => d?.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()));
    setFilteredDepartments(filtered);
  }, [searchTerm, departments]);

  const handleDelete = async (id) => {
    setIsLoading(true);
    try {
      const res = await apiRequestHandler({
        method: "DELETE",
        endPoint: `${url}/department/delete/${id}`,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res?.success) {
        toast.success(res.message);
        const updated = await getAllDepartments();
        setDepartments(updated);
      } else toast.error(res.message);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalOpen = (department = null, mode = "add") => {
    setSelectedDepartment(department);
    setFormMode(mode);
    setShowModal(true);
  };

  const handleModalClose = async () => {
    setShowModal(false);
    setSelectedDepartment(null);
    const updated = await getAllDepartments();
    setDepartments(updated);
  };

  const handleSubmit = async (values, { resetForm }) => {
    const payload = { departmentName: values.departmentName };
    if (formMode === "edit") payload._id = selectedDepartment._id;

    const res = await apiRequestHandler({
      method: "POST",
      endPoint: formMode === "edit" ? "updateDepartment" : "addDepartment",
      data: payload,
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

    if (res?.success) {
      resetForm();
      toast.success(res.message);
      handleModalClose();
    } else {
      toast.error(res.message || "Something went wrong");
    }
  };

  const columns = [
    {
      name: 'Sr No.',
      selector: (row, index) => (currentPage - 1) * 10 + index + 1,
      width: '80px',
    },
    {
      name: 'Title',
      selector: (row) => row.departmentName,
      sortable: true,
    },
    {
      name: 'Created At',
      selector: (row) => row.createdAt,
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <>
          <FaEye title="View" style={{ color: '#064a8c', marginRight: '10px', cursor: 'pointer' }} onClick={() => handleModalOpen(row, 'view')} />
          <FaEdit title="Edit" style={{ color: '#ff9800', marginRight: '10px', cursor: 'pointer' }} onClick={() => handleModalOpen(row, 'edit')} />
          <FaTrash title="Delete" style={{ color: '#f44336', cursor: 'pointer' }} onClick={() => { setDepartmentToDelete(row); setShowDeleteModal(true); }} />
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const isViewMode = formMode === "view";
  const isEditMode = formMode === "edit";

  return (
    <div style={{ minHeight: '100vh', padding: '20px', background: '#f5f7fa' }}>
      <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <h4 style={{ margin: 0 }}>Department Management</h4>
          <button
            onClick={() => handleModalOpen(null, 'add')}
            style={{ background: 'linear-gradient(to bottom right, #163252, #0081ff)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '5px', cursor: 'pointer', fontSize: '14px' }}
          >
            + Add Department
          </button>
        </div>
      </div>

      <div className="modifyTable" style={{ borderRadius: "10px", marginTop: "20px" }}>
        <DataTable
          columns={columns}
          data={filteredDepartments}
          pagination
          highlightOnHover
          striped
          responsive
          progressPending={isLoading}
          onChangePage={setCurrentPage}
          paginationDefaultPage={currentPage}
          noDataComponent={<div style={{ padding: '16px', color: '#999' }}>No departments found</div>}
          subHeader
          subHeaderComponent={
            <input
              type="text"
              placeholder="Search by department title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', width: '300px' }}
            />
          }
        />
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          handleDelete(departmentToDelete?._id);
          setShowDeleteModal(false);
        }}
      />

      {showModal && (
        <div className="modal show fade d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {formMode === "view"
                    ? "View Department"
                    : formMode === "edit"
                    ? "Edit Department"
                    : "Add Department"}
                </h5>
                <button type="button" className="btn-close" onClick={handleModalClose}></button>
              </div>
              <div className="modal-body">
                <Formik
                  enableReinitialize
                  initialValues={{
                    departmentName: selectedDepartment?.departmentName || "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <div className="mb-3">
                        <label className="form-label">Department Title</label>
                        <Field
                          name="departmentName"
                          className="form-control"
                          readOnly={formMode === "view"}
                        />
                        <div className="text-danger small">
                          <ErrorMessage name="departmentName" />
                        </div>
                      </div>
                      {formMode !== "view" && (
                        <div className="text-center">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="submitBtn w-100"
                          >
                            {formMode === "edit" ? "Update Department" : "Submit Department"}
                          </button>
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;